import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type {
  BookableWorkOrder,
  ManualAssignment,
  TechnicianAssignmentSchedule,
  UserProfile,
} from "../../app/generated-axios-client";

export type ScheduleRequestStatus = "idle" | "loading" | "succeeded" | "failed";

// where a booking change was made from, so that page can refresh what it shows
export type ScheduleChangeSource =
  | { ticketId: string }
  | { projectId: string }
  | { board: true };

export type ScheduleTechniciansPayload = {
  technicianIds: number[];
  ticketIds: string[];
  startDate: string;
  endDate: string;
  source: ScheduleChangeSource;
};

export type BoardQuery = {
  startDate: string;
  endDate: string;
  project?: string;
  location?: string;
};

export type OtherBookingsQuery = {
  technicianIds: number[];
  startDate: string;
  endDate: string;
};

export interface AssignmentScheduleState {
  // Loaded here rather than read from the work order meta data: the work order page
  // narrows that data to its client and turbine, which leaves out the technicians.
  technicians: UserProfile[] | null;
  ticketId: string | null;
  ticketSchedules: TechnicianAssignmentSchedule[] | null;
  // the chosen technicians' bookings in the chosen range, shown as a warning in the form
  otherBookings: TechnicianAssignmentSchedule[];
  saveStatus: ScheduleRequestStatus;
  boardQuery: BoardQuery | null;
  boardBookings: TechnicianAssignmentSchedule[] | null;
  manualAssignments: ManualAssignment[] | null;
  bookableWorkOrders: BookableWorkOrder[] | null;
}

const initialState: AssignmentScheduleState = {
  technicians: null,
  ticketId: null,
  ticketSchedules: null,
  otherBookings: [],
  saveStatus: "idle",
  boardQuery: null,
  boardBookings: null,
  manualAssignments: null,
  bookableWorkOrders: null,
};

export const assignmentScheduleSlice = createSlice({
  name: "assignmentSchedule",
  initialState,
  reducers: {
    fetchTechnicians: () => {},
    storeTechnicians: (state, action: PayloadAction<UserProfile[]>) => {
      state.technicians = action.payload;
    },
    fetchTicketSchedules: (state, action: PayloadAction<string>) => {
      if (state.ticketId !== action.payload) {
        state.ticketId = action.payload;
        state.ticketSchedules = null;
      }
    },
    storeTicketSchedules: (
      state,
      action: PayloadAction<{
        ticketId: string;
        schedules: TechnicianAssignmentSchedule[];
      }>
    ) => {
      if (state.ticketId === action.payload.ticketId) {
        state.ticketSchedules = action.payload.schedules;
      }
    },
    fetchOtherBookings: (_state, _action: PayloadAction<OtherBookingsQuery>) => {},
    storeOtherBookings: (
      state,
      action: PayloadAction<TechnicianAssignmentSchedule[]>
    ) => {
      state.otherBookings = action.payload;
    },
    clearOtherBookings: (state) => {
      state.otherBookings = [];
    },
    scheduleTechnicians: (state, _action: PayloadAction<ScheduleTechniciansPayload>) => {
      state.saveStatus = "loading";
    },
    updateScheduleDates: (
      state,
      _action: PayloadAction<{
        scheduleId: string;
        startDate: string;
        endDate: string;
        source: ScheduleChangeSource;
      }>
    ) => {
      state.saveStatus = "loading";
    },
    cancelSchedule: (
      _state,
      _action: PayloadAction<{ scheduleId: string; source: ScheduleChangeSource }>
    ) => {},
    fetchBoard: (state, action: PayloadAction<BoardQuery>) => {
      state.boardQuery = action.payload;
    },
    storeBoard: (
      state,
      action: PayloadAction<{
        bookings: TechnicianAssignmentSchedule[];
        manualAssignments: ManualAssignment[];
      }>
    ) => {
      state.boardBookings = action.payload.bookings;
      state.manualAssignments = action.payload.manualAssignments;
    },
    leaveBoard: (state) => {
      state.boardQuery = null;
      state.boardBookings = null;
      state.manualAssignments = null;
    },
    fetchBookableWorkOrders: () => {},
    storeBookableWorkOrders: (state, action: PayloadAction<BookableWorkOrder[]>) => {
      state.bookableWorkOrders = action.payload;
    },
    setSaveStatus: (state, action: PayloadAction<ScheduleRequestStatus>) => {
      state.saveStatus = action.payload;
    },
  },
});

export const assignmentScheduleActions = assignmentScheduleSlice.actions;
export default assignmentScheduleSlice.reducer;
